import { axiosInstance } from "./index";
import type { StatementStatus } from "./paymentStatement";

export type FinancePaymentStatus =
	| "submitted"
	| "in_review"
	| "in_process"
	| "on_hold"
	| "completed"
	| "failed"
	| "rejected_by_finance";

export interface FinanceCampaignSummary {
	campaignId: string;
	projectCode: string;
	campaignName: string;
	brand: string | null;
	markets: string[];
	campaignManager: string | null;
	paidCount: number;
	totalCount: number;
	disbursement: number;
	overallFee: number;
	currency: string;
}

/**
 * One Finance queue row (Ticket-348). When a Payment Statement has been issued for the instruction
 * (`taxSource === "statement"`), every money and payee value comes from that frozen row. Rows that
 * predate statements (`"live"`) carry in-app totals computed from the country config in force now.
 */
export interface FinancePaymentItem {
	id: string;
	engagementId: string;
	campaignId: string;
	creatorBlockId: string | null;
	creatorName: string;
	market: string | null;
	agreedFee: number;
	currency: string;
	taxSource: "statement" | "live";
	taxRegistered: boolean;
	paymentCountry: string | null;
	taxName: string | null;
	/** Percent, e.g. 10 for 10%. Null when no tax applies. */
	taxRate: number | null;
	taxAmount: number;
	totalPayment: number;
	paymentStatus: FinancePaymentStatus;
	/** Why Finance put the payment on hold; null unless the status is on_hold. */
	paymentStatusReason: string | null;
	paymentStatusUpdatedAt: string | null;
	statementId: string | null;
	statementNumber: string | null;
	statementStatus: StatementStatus | null;
	statementIssuedAt: string | null;
	payeeLegalName: string | null;
	dateSubmitted: string | null;
	dateCompleted: string | null;
	projectCode: string;
	campaignName: string;
	paymentTerms: string;
	campaignManager: string | null;
	campaignSpecialist: string | null;
	approvedBy: string | null;
	approvedAt: string | null;
	financeReference: string;
	bankName: string | null;
	accountName: string | null;
	accountLast4: string | null;
	disbursementReference: string | null;
}

export const agencyFinanceAPI = {
	listCampaigns: async (): Promise<FinanceCampaignSummary[]> => {
		const response = await axiosInstance.get("/agency/finance/campaigns");
		return response.data.campaigns;
	},
	listPayments: async (campaignId: string): Promise<FinancePaymentItem[]> => {
		const response = await axiosInstance.get(`/agency/finance/campaigns/${campaignId}/payments`);
		return response.data.payments;
	},
	/**
	 * Finance acts on the instruction: `completed` needs a transaction reference and `on_hold` needs
	 * a reason (kept on the engagement while held, cleared on resume).
	 */
	updateStatus: async (
		paymentId: string,
		status: FinancePaymentStatus,
		extra: { reference?: string; reason?: string } = {},
	): Promise<FinancePaymentItem> => {
		const response = await axiosInstance.patch(`/agency/finance/payments/${paymentId}/status`, {
			status,
			...extra,
		});
		return response.data.payment;
	},
};
